import { Loading } from 'components/shared';
import { Button, Dropdown, Tabs } from 'components/ui';
import InputModern from 'components/ui/Input/InputModern';
import TabContent from 'components/ui/Tabs/TabContent';
import TabList from 'components/ui/Tabs/TabList';
import TabNav from 'components/ui/Tabs/TabNav';
import { CompanyContext } from 'contexts/CompanyContext';
import { MediaContext } from 'contexts/MediaContext';
import React, { useContext, useEffect, useState } from 'react'
import { Trans } from 'react-i18next';
import { HiOutlineFilter, HiOutlinePhotograph } from 'react-icons/hi';
import { HiOutlineMagnifyingGlass, HiOutlineRectangleGroup } from 'react-icons/hi2';
import Masonry, { ResponsiveMasonry } from 'react-responsive-masonry';
import getFileType from 'utils/fileType';
import { useStockMedia } from 'utils/hooks/useStockMedia';
import MediaElement from 'views/calendar/MediaElement';
import MediaUpload from 'views/calendar/MediaUpload';
import { MdImageSearch } from 'react-icons/md';
import { BsStars } from 'react-icons/bs';
import AIImageBody from 'views/ai_image/AIImageBody';
import capitalize from 'components/ui/utils/capitalize';
import { LuImage, LuRectangleHorizontal, LuRectangleVertical } from 'react-icons/lu';
import { TbGif, TbPhotoVideo, TbSquare, TbVideo } from 'react-icons/tb';
import Canva from './Canva';
import { AI_AVATARS_ENABLED, CANVA_ENABLED } from 'components/ui/utils/constant';
import { RiImageAiLine, RiVideoOnAiLine } from 'react-icons/ri';
import AIVideoBody from 'views/ai_video/AIVideoBody';
import { motion, AnimatePresence } from 'framer-motion';
import useDarkMode from 'utils/hooks/useDarkMode';
import AIAvatarVideosBody from 'views/ai_avatar_videos/AIAvatarVideosBody';
import { HiUserCircle, HiFire } from 'react-icons/hi2';
import ViralShortsBody from 'views/viral_shorts/ViralShortsBody';
import FolderChips from './components/FolderChips';

const ExploreMedia = ({ 
    isSelectable = false, 
    isMultipleSelect = false,
    handleOnSelect = undefined, 
    handleOnUnselect = undefined, 
    initialSelectedAssets = undefined,
    allowedTypes = ["image", "video", "gif"], 
    allowedOrientations = ["landscape", "portrait", "square"],
    showMyAssets = false, 
    showAIAssets = true,
    showStockAssets = true,
    showCanva = false,
    showTemplates = false,
    className = "",
    breakpoints = { 350: 1, 512: 2, 750: 2, 900: 3, 1100: 4, 1600: 4 },
    stockInputClassName = "",
    simplificated = false,
    onClose = undefined
}) => {  

    const perPage = 30;
    const [isDarkMode] = useDarkMode();
    const {company} = useContext(CompanyContext);
    const [media, setMedia] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const { isLoadingMyMedia, myMedia, filtersMyMedia, setFiltersMyMedia, setFiltersMyMediaDrawerOpened, hasMoreMyMedia, loadMoreMyMedia } = useContext(MediaContext);
    const [query, setQuery] = useState("");
    const [myMediaQuery, setMyMediaQuery] = useState(filtersMyMedia?.query || "");
    const [page, setPage] = useState(0);
    const [selectedAssets, setSelectedAssets] = useState([]);
    const [filteredType, setFilteredType] = useState(allowedTypes?.length == 3 ? undefined : allowedTypes[0]);
    const [filteredOrientation, setFilteredOrientation] = useState(allowedOrientations?.length == 3 ? undefined : allowedOrientations[0])
    const { getStockMedia } = useStockMedia();
    const [selectedCanvaDesigns, setSelectedCanvaDesigns] = useState([]);
    const [activeTab, setActiveTab] = useState(showMyAssets ? 'tabMyMedia' : showStockAssets ? 'tabExplore' : 'tabAI');

    const getMedia = (replace = false, _page = page) => {
        _page = _page+1;
        if (replace) {
            _page = 1;
            setMedia([]);
        };
        setPage(_page);
        if (!isLoading) {
            setIsLoading(true);
            getStockMedia(perPage, _page, query, filteredOrientation, allowedTypes?.filter(e => filteredType ? e == filteredType : true)).then((data) => {
                setMedia(replace ? [...(data ?? [])] : [...media, ...(data ?? [])]);
            }).finally(() => {
                setIsLoading(false);
            });
        }
    }

    const prevHandleOnSelect = (originalSrc, asset) => {
        if (!isMultipleSelect) {
            handleOnSelect(originalSrc, asset)
        } else {
            if (!!handleOnSelect(originalSrc, asset)) {
                setSelectedAssets([...selectedAssets, { originalSrc, asset }]);
            }
        }
    }

    const prevHandleOnUnselect = (originalSrc, asset) => {
        if (isMultipleSelect) {
            setSelectedAssets(selectedAssets.filter(i => i.originalSrc != originalSrc));
            !!handleOnUnselect && handleOnUnselect(originalSrc, asset);
        }
    }

    const handleSelectCanva = (urls, designId) => {
        setSelectedCanvaDesigns([...selectedCanvaDesigns, { designId, urls }]);

        urls.forEach(url => {
            handleOnSelect(url, null);
        });
    }

    const handleUnselectCanva = (designId) => {
        const urls = selectedCanvaDesigns.find(i => i.designId == designId)?.urls;

        setSelectedCanvaDesigns(selectedCanvaDesigns.filter(i => i.designId != designId));
        urls.forEach(url => {
            handleOnUnselect(url, null);
        });
    }


    useEffect(() => {
        getMedia(true, 0);
    }, [filteredType, filteredOrientation, company?.id]);

    useEffect(() => {
        // if (!selectedAssets?.length) {
        setSelectedAssets(initialSelectedAssets || []);
        // }
    }, [initialSelectedAssets]);

    // useEffect(() => {
    //     if (media) {
    //         localStorage.setItem(`mediaMedia${company?.id}${JSON.stringify(allowedTypes)}`, JSON.stringify(media));
    //     }
    // }, [media]);

    // Tab configuration with enhanced metadata
    const tabConfig = [
        {
            value: 'tabMyMedia',
            icon: <HiOutlinePhotograph className="text-lg" />,
            label: <Trans i18nKey="exploreMedia.myAssets">My Assets</Trans>,
            gradient: 'from-blue-500 to-indigo-500',
            show: showMyAssets
        },
        {
            value: 'tabExplore',
            icon: <MdImageSearch className="text-lg" />,
            label: <Trans i18nKey="exploreMedia.explore">Stock Assets</Trans>,
            gradient: 'from-green-500 to-emerald-500',
            show: showStockAssets
        },
        {
            value: 'tabAI',
            icon: <RiImageAiLine className="text-base" />,
            label: <Trans i18nKey="exploreMedia.aiGenerated">AI Images</Trans>,
            gradient: 'from-purple-500 to-pink-500',
            show: showAIAssets && allowedTypes.includes("image")
        },
        {
            value: 'tabAIVideo',
            icon: <RiVideoOnAiLine className="text-base" />,
            label: 'AI Videos',
            gradient: 'from-primary-500 to-blue-500',
            show: showAIAssets && allowedTypes.includes("video")
        },
        ...(AI_AVATARS_ENABLED ? [{
            value: 'tabAIAvatarVideos',
            icon: <HiUserCircle className="text-lg" />,
            label: 'AI Avatar',
            gradient: 'from-violet-500 to-purple-500',
            show: showAIAssets && allowedTypes.includes("video")
        }, {
            value: 'tabViralShorts',
            icon: <HiFire className="text-lg" />,
            label: 'Viral Shorts',
            gradient: 'from-green-500 to-emerald-600',
            show: showAIAssets && allowedTypes.includes("video")
        }] : []),
        {
            value: 'canva',
            icon: <img src="/img/socials/canva.svg" alt="Canva" className='w-4 h-4 max-w-none' />,
            label: <Trans i18nKey="exploreMedia.canva">Canva</Trans>,
            gradient: 'from-cyan-500 to-blue-500',
            show: !!CANVA_ENABLED && showCanva
        }
    ];

    // Count visible tabs
    const visibleTabs = tabConfig.filter(tab => tab.show);
    const showTabNavigation = visibleTabs.length > 1;

    return (
        <>
            <Tabs value={activeTab} onChange={(val) => setActiveTab(val)} onClick={e => e.stopPropagation()}>
                {/* Enhanced Tab Navigation */}
                {showTabNavigation && (
                <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`relative ${simplificated ? '' : 'mb-6 pb-2'}`}
                >
                    <TabList className={`!pb-0 !border-0 !overflow-visible ${
                        simplificated
                            ? 'p-0 !px-0 gap-1'
                            : '!pt-2 !pb-2 !px-2 gap-2 bg-gray-100/80 dark:bg-gray-800/50 rounded-2xl backdrop-blur-xl'
                    } flex flex-wrap`}>
                        {visibleTabs.map((tab, index) => (
                            <TabNav 
                                key={tab.value}
                                className={`
                                    group relative
                                    ${simplificated ? 'flex-1 p-2 overflow-hidden' : 'flex-1 lg:flex-auto px-6 py-2'}
                                    transition-all duration-300
                                    ${activeTab === tab.value 
                                        ? simplificated
                                            ? 'bg-white dark:bg-gray-800 shadow-md'
                                            : 'bg-white dark:bg-gray-700 shadow-xl scale-105'
                                        : simplificated
                                            ? 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                                            : 'hover:bg-white/50 dark:hover:bg-gray-700/50 hover:scale-102'
                                    }
                                    ${simplificated ? 'rounded-lg' : 'rounded-xl'}
                                    border ${activeTab === tab.value 
                                        ? isDarkMode 
                                            ? 'border-white/20' 
                                            : 'border-gray-200'
                                        : 'border-transparent'
                                    }
                                `}
                                value={tab.value}
                            >
                                {/* Gradient background for active tab */}
                                {activeTab === tab.value && !simplificated && (
                                    <motion.div
                                        layoutId="activeTabExplore"
                                        className={`absolute inset-0 bg-gradient-to-br ${tab.gradient} opacity-5`}
                                        transition={{
                                            type: "spring",
                                            stiffness: 380,
                                            damping: 30
                                        }}
                                    />
                                )}
                                
                                {/* Hover gradient effect */}
                                {!simplificated && (
                                    <div 
                                        className={`absolute inset-0 bg-gradient-to-br ${tab.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-300`}
                                    />
                                )}
                                
                                {/* Tab content */}
                                <div className="relative z-10 flex items-center justify-center gap-2">
                                    {/* Icon wrapper with gradient */}
                                    <motion.div
                                        whileHover={{ scale: 1.15, rotate: activeTab === tab.value ? 0 : 5 }}
                                        whileTap={{ scale: 0.95 }}
                                        className={`
                                            flex items-center justify-center
                                            ${activeTab === tab.value
                                                ? simplificated
                                                    ? 'text-primary-600 dark:text-primary-400'
                                                    : `p-1.5 rounded-xl bg-gradient-to-br ${tab.gradient} shadow-lg`
                                                : simplificated
                                                    ? 'text-gray-600 dark:text-gray-400'
                                                    : 'text-gray-600 dark:text-gray-400 group-hover:text-gray-900 dark:group-hover:text-white'
                                            }
                                            transition-all duration-300
                                        `}
                                    >
                                        {activeTab === tab.value && !simplificated ? (
                                            <div className="text-white">
                                                {tab.icon}
                                            </div>
                                        ) : (
                                            tab.icon
                                        )}
                                    </motion.div>
                                    
                                    {/* Label */}
                                    {!simplificated && (
                                        <span className={`
                                            hidden lg:inline font-semibold text-sm whitespace-nowrap
                                            ${activeTab === tab.value
                                                ? 'text-gray-900 dark:text-white'
                                                : 'text-gray-600 dark:text-gray-400 group-hover:text-gray-900 dark:group-hover:text-white'
                                            }
                                            transition-colors duration-300
                                        `}>
                                            {tab.label}
                                        </span>
                                    )}
                                </div>
                                
                                {/* Bottom highlight bar for active tab */}
                                {activeTab === tab.value && simplificated && (
                                    <motion.div
                                        layoutId="simpleActiveBarExplore"
                                        className={`absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r ${tab.gradient}`}
                                        transition={{
                                            type: "spring",
                                            stiffness: 380,
                                            damping: 30
                                        }}
                                    />
                                )}
                        </TabNav>
                        ))}
                </TabList>
                </motion.div>
                )}
                
                <div className={simplificated || !showTabNavigation ? "mt-4" : ""}>
                    <AnimatePresence mode="wait">
                        <TabContent className="flex flex-col gap-4" value="tabMyMedia">
                            <motion.div
                                key="tabMyMedia"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -20 }}
                                transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                                className='w-[auto] h-full !overflow-y-auto'
                            >
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.1 }}
                                className='pb-6'
                            >
                                <MediaUpload />
                            </motion.div>
                            <FolderChips />
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.2 }}
                                className='flex items-center pb-4 gap-2'
                            >
                                <div className='p-0 relative flex-1 bg-gray-100 dark:bg-gray-700 rounded-2xl'> 
                                    <InputModern 
                                        onChange={(e) => setMyMediaQuery(e.target.value)} 
                                        className="!px-5 !py-5 !h-auto rounded-2xl bg-gray-100 dark:bg-gray-700 shadow-sm hover:shadow-md transition-all duration-200" 
                                        placeholder="Search your media..." 
                                        onKeyUp={(e) => e.key == 'Enter' ? setFiltersMyMedia({...filtersMyMedia, query: myMediaQuery }) : void 0}
                                    />
                                    <div className='flex gap-2 items-center !absolute right-2.5 top-1/2 translate-y-[-50%]'>
                                        <motion.div
                                            whileHover={{ scale: 1.05 }}
                                            whileTap={{ scale: 0.95 }}
                                        >
                                        <Button 
                                            loading={isLoading}
                                                variant="solid" 
                                            size="md" 
                                                className="flex gap-2 items-center !text-xs bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 !text-white shadow-md hover:shadow-lg transition-all duration-200"
                                            onClick={() => setFiltersMyMedia({...filtersMyMedia, query: myMediaQuery })}
                                            >
                                                <HiOutlineMagnifyingGlass className='text-xl' />
                                                <span>Search</span>
                                            </Button>
                                        </motion.div>
                                        <div className='shrink-0'>
                                            <Button 
                                                className="hidden lg:block shadow-sm hover:shadow-md transition-shadow" 
                                                onClick={() => setFiltersMyMediaDrawerOpened(true)} 
                                                icon={<HiOutlineFilter />} 
                                                filterQuantity={Object.keys(filtersMyMedia)?.length}
                                            >
                                                &nbsp; <Trans i18nKey={`media.filters`}>Filters</Trans>
                                            </Button>
                                            <Button 
                                                className="lg:hidden shadow-sm hover:shadow-md transition-shadow" 
                                                onClick={() => setFiltersMyMediaDrawerOpened(true)} 
                                                icon={<HiOutlineFilter />} 
                                                filterQuantity={Object.keys(filtersMyMedia)?.length} 
                                            />
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                            {isLoading && media?.length == 0 && <Loading type={`element`} />}
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.3 }}
                            >
                            <ResponsiveMasonry className='my-4 mt-0' columnsCountBreakPoints={breakpoints}>
                                <Masonry gutter='0.75rem'>
                                    {myMedia && myMedia.map((item, key) => {
                                    if (!allowedTypes?.includes(getFileType(item?.original))) return <></>;
                                        return (
                                            <MediaElement 
                                                handleOnSelect={prevHandleOnSelect} 
                                                handleOnUnselect={prevHandleOnUnselect} 
                                                selected={!!selectedAssets.find(e => e?.asset?.id == item?.id)} 
                                                isSelectable={isSelectable} 
                                                key={key} 
                                                asset={item} 
                                                onError={(e) => {
                                                    try { e.target.parentElement.parentElement.style = "display: none" } catch (err) {}
                                                }}
                                            />
                                        );
                                    })}
                                </Masonry>
                            </ResponsiveMasonry>
                            </motion.div>
                            {hasMoreMyMedia && (
                                <motion.div 
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className='flex w-full justify-center my-4'
                                >
                                    <Button 
                                        loading={isLoadingMyMedia} 
                                        disabled={isLoadingMyMedia} 
                                        variant="twoTone" 
                                        onClick={loadMoreMyMedia}
                                        className="shadow-sm hover:shadow-md transition-all"
                                    >
                                        Load More
                                    </Button>
                                </motion.div>
                            )}
                            </motion.div>
                    </TabContent>
                    <TabContent className="flex flex-col gap-4" value="tabExplore">
                            <motion.div
                                key="tabExplore"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -20 }}
                                transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                            >
                        <motion.div 
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.1 }}
                            className={`p-0 relative pb-4 flex flex-col ${simplificated ? 'gap-0' : 'gap-0 md:flex-row md:items-stretch md:items-center'}`}
                        >
                            <Dropdown
                                renderTitle={
                                    <Dropdown.Item className={`flex items-center gap-2.5 py-2 px-4 border-b md:border-b-none md:border-b-0 md:border-r border-gray-200 dark:border-gray-600 rounded-t-2xl rounded-b-none ${simplificated ? '!rounded-2xl !h-12' : 'md:!rounded-r-none md:rounded-l-2xl !h-14'} bg-gray-100 dark:bg-gray-700 shadow-sm hover:shadow-md transition-all duration-200 ${stockInputClassName}`}>
                                        {filteredType == "image" 
                                            ? <LuImage className='text-2xl text-primary-600' />
                                            : filteredType == "gif" 
                                                ? <TbGif className='text-2xl text-purple-500' />
                                                : filteredType == "video"
                                                    ? <TbVideo className='text-2xl text-fuchsia-600' />
                                                    : <TbPhotoVideo className='text-2xl' />
                                        }
                                        <span>{filteredType ? capitalize(filteredType) : "All Media"}</span>
                                    </Dropdown.Item>
                                }
                            >
                                <Dropdown.Item onClick={() => setFilteredType(undefined)} className={`flex items-center gap-2.5 py-2 px-4 rounded-t-2xl rounded-b-none`}>
                                    <TbPhotoVideo className='text-2xl' />
                                    <span>All Media</span>
                                </Dropdown.Item>
                                {allowedTypes?.map((item, key) => (
                                    <Dropdown.Item key={key} onClick={() => setFilteredType(item)} className="flex items-center gap-2.5 py-2 px-4 rounded-t-2xl rounded-b-none">
                                        {item == "image" 
                                            ? <LuImage className='text-2xl text-primary-600' />
                                            : item == "gif" 
                                                ? <TbGif className='text-2xl text-purple-500' />
                                                : item == "video"
                                                    ? <TbVideo className='text-2xl text-fuchsia-600' />
                                                    : <></>
                                        }
                                        <span>{capitalize(item)}</span>
                                    </Dropdown.Item>
                                ))}
                            </Dropdown>
                            <Dropdown
                                renderTitle={
                                    <Dropdown.Item className={`flex items-center gap-2.5 py-2 px-4 border-b md:border-b-none md:border-b-0 md:border-r border-gray-200 dark:border-gray-600 ${simplificated ? '!rounded-2xl !h-12' : '!rounded-none !h-14'} bg-gray-100 dark:bg-gray-700 shadow-sm hover:shadow-md transition-all duration-200 ${stockInputClassName}`}>
                                        {filteredOrientation == "square" 
                                            ? <TbSquare className='text-2xl text-primary-600' />
                                            : filteredOrientation == "landscape" 
                                                ? <LuRectangleHorizontal className='text-2xl text-purple-500' />
                                                : filteredOrientation == "portrait"
                                                    ? <LuRectangleVertical className='text-2xl text-fuchsia-600' />
                                                    : <HiOutlineRectangleGroup className='text-2xl' />
                                        }
                                        <span>{filteredOrientation ? capitalize(filteredOrientation) : "All Orientations"}</span>
                                    </Dropdown.Item>
                                }
                            >
                                <Dropdown.Item onClick={() => setFilteredOrientation(undefined)} className="flex items-center gap-2.5 py-2 px-4 rounded-t-2xl rounded-b-none">
                                    <HiOutlineRectangleGroup className='text-2xl' />
                                    <span>All Orientations</span>
                                </Dropdown.Item>
                                {allowedOrientations?.map((item, key) => (
                                    <Dropdown.Item key={key} onClick={() => setFilteredOrientation(item)} className={`flex items-center gap-2.5 py-2 px-4 rounded-t-2xl ${simplificated ? '!rounded-2xl' : '!rounded-none'}`}>
                                        {item == "square" 
                                            ? <TbSquare className='text-2xl text-primary-600' />
                                            : item == "landscape" 
                                                ? <LuRectangleHorizontal className='text-2xl text-purple-500' />
                                                : item == "portrait"
                                                    ? <LuRectangleVertical className='text-2xl text-fuchsia-600' />
                                                    : <></>
                                        }
                                        <span>{capitalize(item)}</span>
                                    </Dropdown.Item>
                                ))}
                            </Dropdown>
                            <InputModern 
                                onChange={(e) => setQuery(e.target.value)} 
                                className={`!px-5 !rounded-none text-sm bg-gray-100 dark:bg-gray-700 shadow-sm transition-all duration-200 focus-within:shadow-md ${stockInputClassName} ${simplificated ? '!h-10 !py-4' : '!h-14 !py-5'}`} 
                                fieldsetClassName={`flex-1 ${simplificated ? '!rounded-2xl' : '!rounded-none'}`}
                                placeholder="Search stock media..." 
                                onKeyUp={(e) => e.key == 'Enter' ? getMedia(true) : void 0}
                            />
                            <motion.div
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                            >
                            <Button 
                                loading={isLoading}
                                variant="solid" 
                                size="md" 
                                className={`flex gap-2 items-center justify-center font-semibold !py-4 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 !text-white shadow-lg hover:shadow-xl transition-all duration-200 ${simplificated ? '!rounded-2xl !h-12' : '!rounded-t-none md:!rounded-tr-2xl md:!rounded-l-none !h-14'} !px-6 w-full lg:w-auto`}
                                onClick={() => getMedia(true)}
                            >
                                <HiOutlineMagnifyingGlass className='text-xl' />
                                <span>Search</span>
                            </Button>
                            </motion.div>
                        </motion.div>
                        {isLoading && media?.length == 0 && <Loading type={`element`} />}
                        <motion.div 
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.2 }}
                            className='w-[auto] h-full !overflow-y-auto'
                        >
                            <ResponsiveMasonry className='my-4 mt-0' columnsCountBreakPoints={breakpoints}>
                                <Masonry gutter='0.75rem'>
                                    {!!media?.length && media.map((item, key) => {
                                        return (
                                            <MediaElement 
                                                isDraggable={simplificated ? true : undefined}
                                                handleOnSelect={prevHandleOnSelect} 
                                                handleOnUnselect={prevHandleOnUnselect} 
                                                selected={!!selectedAssets?.find(e => e.originalSrc == item?.original)} 
                                                isSelectable={isSelectable} 
                                                aspectRatio={item?.aspectRatio} 
                                                src={item?.src} 
                                                originalSrc={item?.original} 
                                                mediaDescription={item?.description} 
                                                isStock={item?.isStock} 
                                                onError={(e) => {
                                                    try { e.target.parentElement.parentElement.style = "display: none" } catch (err) {}
                                                }}
                                                {...item} 
                                            />
                                        )
                                    })}
                                </Masonry>
                            </ResponsiveMasonry>
                            {!!media?.length && (
                                <motion.div
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className='flex w-full justify-center my-4'
                                >
                                    <Button 
                                        variant="twoTone" 
                                        loading={isLoading} 
                                        onClick={() => getMedia(false)}
                                        className="shadow-sm hover:shadow-md transition-all"
                                    >
                                        Load More
                                    </Button>
                                </motion.div>
                            )}
                        </motion.div>
                    </motion.div>
                </TabContent>
                    <TabContent className="flex flex-col gap-4" value="tabAI">
                            <motion.div
                                key="tabAI"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -20 }}
                                transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                                className="flex flex-col gap-4"
                            >
                        <AIImageBody 
                            inputClassName='!bg-gray-100 dark:!bg-gray-700 !ring-transparent'
                            // columnBreakPoints={{ 350: 1, 1024: 2 }}
                            showPreview={false}
                            handleOnSelect={prevHandleOnSelect} 
                            handleOnUnselect={prevHandleOnUnselect} 
                            selectedImages={selectedAssets}
                            isSelectable={isSelectable} 
                            showCreate={false}
                        />
                            </motion.div>
                    </TabContent>
                    <TabContent className="flex flex-col gap-4" value="tabAIVideo">
                            <motion.div
                                key="tabAIVideo"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -20 }}
                                transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                                className="flex flex-col gap-4"
                            >
                        <AIVideoBody 
                            // inputClassName='!bg-gray-100 dark:!bg-gray-700 !ring-transparent'
                            // columnBreakPoints={{ 350: 1, 1024: 2 }}
                            showPreview={false}
                            handleOnSelect={prevHandleOnSelect} 
                            handleOnUnselect={prevHandleOnUnselect} 
                            selectedVideos={selectedAssets}
                            isSelectable={isSelectable} 
                            showCreate={false}
                        />
                            </motion.div>
                    </TabContent>
                    <TabContent className="flex flex-col gap-4" value="tabAIAvatarVideos">
                        <motion.div
                            key="tabAIAvatarVideos"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                            className="flex flex-col gap-4"
                        >
                            <AIAvatarVideosBody 
                                showPreview={false}
                                handleOnSelect={prevHandleOnSelect} 
                                handleOnUnselect={prevHandleOnUnselect} 
                                selectedVideos={selectedAssets}
                                isSelectable={isSelectable} 
                                simplificated={simplificated}
                                onClose={onClose}
                                inModal={true}
                            />
                        </motion.div>
                    </TabContent>
                    <TabContent className="flex flex-col gap-4" value="tabViralShorts">
                        <motion.div
                            key="tabViralShorts"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                            className="flex flex-col gap-4"
                        >
                            <ViralShortsBody 
                                showPreview={false}
                                handleOnSelect={prevHandleOnSelect} 
                                handleOnUnselect={prevHandleOnUnselect} 
                                selectedVideos={selectedAssets}
                                isSelectable={isSelectable} 
                                simplificated={simplificated}
                                onClose={onClose}
                                inModal={true}
                                showCreateButton={false}
                            />
                        </motion.div>
                    </TabContent>
                    <TabContent className="flex flex-col gap-4" value="canva">
                        <motion.div
                            key="canva"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                        >
                            <Canva 
                                handleOnSelect={handleSelectCanva} 
                                handleOnUnselect={handleUnselectCanva} 
                                selectedImages={selectedCanvaDesigns}
                                isSelectable={isSelectable} 
                            />
                        </motion.div>
                    </TabContent>
                    </AnimatePresence>
                </div>
            </Tabs>
        </>
    );
}

export default ExploreMedia;
